import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { apiCall } from '../../api/client';
import { PhoneInput } from '../../components/common/PhoneInput';
import { Spinner } from '../../components/common/Spinner';
import { SmsConsentDisclosure } from '../../components/common/SmsConsentDisclosure';
import { SMS_CONSENT_SCRIPT_PATIENT, VISIT_MEDIUM_OPTIONS } from '../../constants';
import { initEmbedAutoResize } from '../../embed/postHeight';
import type { Branding } from '../../types';

type Medium = 'video' | 'phone' | 'sms';

const MEDIUM_LABELS: Record<Medium, string> = {
  video: 'Video Visit',
  phone: 'Phone Call',
  sms: 'Text Message',
};
import { trackBookingStep, trackBookingCompleted } from '../../utils/analytics';

interface ProviderInfo {
  uid: string;
  name: string;
  headshotUrl?: string;
}

interface Slot {
  time: string;
  timestamp: number;
  providerUid: string;
  providerName: string;
}

function formatTime(time24: string): string {
  const [h, m] = time24.split(':').map(Number);
  const ampm = h < 12 ? 'AM' : 'PM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function formatPhoneDashed(num: string | null | undefined): string {
  const d = (num || '').replace(/\D/g, '').replace(/^1/, '');
  if (d.length !== 10) return num || '';
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

export function BookPage() {
  const { practiceId } = useParams();
  const [searchParams] = useSearchParams();
  const preselectedProvider = searchParams.get('provider');

  const [loading, setLoading] = useState(true);
  const [branding, setBranding] = useState<Branding | undefined>();
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [step, setStep] = useState(1);
  useEffect(() => { trackBookingStep(`step_${step}`); }, [step]);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');

  // State
  const [selectedProviders, setSelectedProviders] = useState<ProviderInfo[]>([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [slotDuration, setSlotDuration] = useState(30);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  // Form
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [medium, setMedium] = useState<Medium | ''>('');
  const [booking, setBooking] = useState(false);

  // Confirmation
  const [confirmData, setConfirmData] = useState<{
    providerName: string;
    joinUrl: string | null;
    duration: number;
    medium?: Medium;
    dialIn?: { phone: string; pin: string; providerPin?: string; patientPin?: string } | null;
    smsNumber?: string | null;
  } | null>(null);

  // Apply branding
  useEffect(() => {
    if (!branding) return;
    const root = document.documentElement;
    if (branding.primaryColor) root.style.setProperty('--primary-color', branding.primaryColor);
    return () => { root.style.removeProperty('--primary-color'); };
  }, [branding?.primaryColor]);

  // Iframe auto-resize bridge for partner embeds — no-op when not iframed.
  useEffect(() => {
    return initEmbedAutoResize();
  }, []);

  // Load practice data
  useEffect(() => {
    if (!practiceId) { setLoading(false); return; }
    Promise.all([
      apiCall<{ practiceName?: string; primaryColor?: string; logoUrl?: string }>(`/practices/${practiceId}/public`, { auth: false }),
      apiCall<{ providers: ProviderInfo[] }>(`/practices/${practiceId}/providers`, { auth: false }),
    ]).then(([practice, provData]) => {
      setBranding({
        practiceName: practice.practiceName,
        primaryColor: practice.primaryColor,
        logoUrl: practice.logoUrl,
      });
      const provs = provData.providers || [];
      setProviders(provs);

      // Handle preselected provider
      if (preselectedProvider) {
        const found = provs.find((p) => p.uid === preselectedProvider);
        if (found) {
          setSelectedProviders([found]);
          setStep(2);
          return;
        }
      }
      // Skip provider selection if only one provider exists
      if (provs.length === 1) {
        setSelectedProviders([provs[0]]);
        setStep(2);
        return;
      }
      setLoading(false);
    }).catch(() => {
      setLoadError('Practice not found.');
    }).finally(() => {
      setLoading(false);
    });
  }, [practiceId, preselectedProvider]);

  // Load slots when entering step 3
  useEffect(() => {
    if (step !== 3 || !selectedDate || selectedProviders.length === 0) return;
    setSlotsLoading(true);
    const fetches = selectedProviders.map(async (p) => {
      try {
        const resp = await apiCall<{ slots: Array<{ time: string; timestamp: number }>; duration?: number }>(
          `/providers/${p.uid}/slots?date=${selectedDate}`, { auth: false }
        );
        if (resp.duration) setSlotDuration(resp.duration);
        return (resp.slots || []).map((s) => ({ ...s, providerUid: p.uid, providerName: p.name }));
      } catch { return []; }
    });
    Promise.all(fetches).then((results) => {
      const allSlots = results.flat().sort((a, b) => a.timestamp - b.timestamp);
      // Deduplicate in "next available" mode
      if (selectedProviders.length > 1) {
        const seen = new Map<string, Slot>();
        for (const s of allSlots) {
          if (!seen.has(s.time)) seen.set(s.time, s);
        }
        setSlots(Array.from(seen.values()));
      } else {
        setSlots(allSlots);
      }
      setSlotsLoading(false);
    });
  }, [step, selectedDate, selectedProviders]);

  if (!practiceId) {
    return (
      <div className="book-container" style={{ maxWidth: '480px', margin: '0 auto', padding: '2rem 1rem' }}>
        <div className="card text-center"><p className="text-muted">Practice not found.</p></div>
      </div>
    );
  }

  if (loading) return <Spinner fullPage />;
  if (loadError) {
    return (
      <div className="book-container" style={{ maxWidth: '480px', margin: '0 auto', padding: '2rem 1rem' }}>
        <div className="card text-center"><p className="text-muted">{loadError}</p></div>
      </div>
    );
  }

  const todayStr = new Date().toISOString().split('T')[0];
  const maxDate = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  // Step dots — hide step 1 when provider selection is skipped (preselected or only one provider)
  // Steps: 1=provider, 2=date, 3=time, 4=visit medium, 5=confirm/info, 6=booked
  const skipProviderStep = !!preselectedProvider || providers.length === 1;
  const visibleSteps = skipProviderStep ? [2, 3, 4, 5] : [1, 2, 3, 4, 5];
  const stepDots = (
    <div style={{ display: step === 6 ? 'none' : 'flex', gap: '0.5rem', justifyContent: 'center', marginBottom: '1.5rem' }}>
      {visibleSteps.map((s) => (
        <div
          key={s}
          style={{
            width: '8px', height: '8px', borderRadius: '50%',
            background: s === step ? 'var(--primary-color)' : s < step ? 'var(--primary-color)' : 'var(--border-color)',
            opacity: s < step ? 0.5 : 1,
          }}
        />
      ))}
    </div>
  );

  const handleBook = async () => {
    setError('');
    if (!name.trim()) { setError('Please enter your name.'); return; }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError('Please enter a valid email address.'); return; }
    if (!medium) { setError('Please choose a visit type.'); return; }
    if (medium === 'sms') {
      if (!phone.trim()) { setError('A mobile phone number is required for text-message visits.'); return; }
      if (!smsConsent) { setError('Please consent to receive SMS messages to book a text-message visit.'); return; }
    }
    if (!selectedSlot) return;

    setBooking(true);
    try {
      const body: Record<string, unknown> = {
        practiceId,
        providerUid: selectedSlot.providerUid,
        scheduledAt: selectedSlot.timestamp,
        patientName: name.trim(),
        patientEmail: email.trim() || undefined,
        medium,
      };
      if (phone.trim() && smsConsent) {
        body.patientPhone = phone.trim();
        body.smsConsent = { source: 'patient_direct', consentedAt: Date.now() };
      }
      const data = await apiCall<{
        providerName: string;
        joinUrl: string | null;
        duration: number;
        medium?: Medium;
        dialIn?: { phone: string; pin: string; providerPin?: string; patientPin?: string } | null;
        smsNumber?: string | null;
      }>('/visits/request', { method: 'POST', body, auth: false });
      setConfirmData(data);
      trackBookingCompleted(practiceId || '');
      setStep(6);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Booking failed');
    } finally {
      setBooking(false);
    }
  };

  return (
    <div style={{ maxWidth: '480px', margin: '0 auto', padding: '2rem 1rem' }}>
      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        {branding?.logoUrl && (
          <img src={branding.logoUrl} alt="" style={{ maxWidth: '180px', maxHeight: '80px', margin: '0 auto 0.75rem', display: 'block' }} />
        )}
        <h1 style={{ fontSize: '1.5rem', marginBottom: '0.125rem' }}>{branding?.practiceName || 'Telehealth'}</h1>
        <p className="text-muted text-sm">Book a Telehealth Visit</p>
      </div>

      {stepDots}

      {/* Step 1: Pick Provider */}
      {step === 1 && (
        <div className="card">
          <h2 style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>Choose a Provider</h2>
          {providers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem 1rem', color: 'var(--text-muted)' }}>No providers available for booking.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {providers.length > 1 && (
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.875rem 1rem', border: '2px solid var(--border-color)', borderRadius: 'var(--radius)', cursor: 'pointer' }}
                  onClick={() => { setSelectedProviders([...providers]); setStep(2); }}
                >
                  <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'var(--accent-color, #10B981)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: '0.875rem', flexShrink: 0 }}>&#9733;</div>
                  <div><div style={{ fontWeight: 500 }}>Next Available</div><div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Any provider</div></div>
                </div>
              )}
              {providers.map((p) => {
                const initials = p.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
                return (
                  <div
                    key={p.uid}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.875rem 1rem', border: '2px solid var(--border-color)', borderRadius: 'var(--radius)', cursor: 'pointer' }}
                    onClick={() => { setSelectedProviders([p]); setStep(2); }}
                  >
                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'var(--primary-color)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: '0.875rem', flexShrink: 0 }}>{initials}</div>
                    <div style={{ fontWeight: 500 }}>{p.name}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Step 2: Pick Date */}
      {step === 2 && (
        <div className="card">
          {!skipProviderStep && (
            <a style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: 'var(--primary-color)', textDecoration: 'none', fontSize: '0.875rem', marginBottom: '1rem', cursor: 'pointer' }} onClick={() => setStep(1)}>&larr; Change provider</a>
          )}
          <h2 style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>Pick a Date</h2>
          <p className="text-sm text-muted" style={{ marginBottom: '0.75rem' }}>
            {selectedProviders.length === 1 ? `With ${selectedProviders[0].name}` : 'Any available provider'}
          </p>
          <div className="form-group">
            <input type="date" value={selectedDate} min={todayStr} max={maxDate} onChange={(e) => setSelectedDate(e.target.value)} style={{ width: '100%' }} />
          </div>
          <button className="btn btn--primary btn--full" style={{ marginTop: '1rem' }} onClick={() => { if (selectedDate) setStep(3); }} disabled={!selectedDate}>Next</button>
        </div>
      )}

      {/* Step 3: Pick Time Slot */}
      {step === 3 && (
        <div className="card">
          <a style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: 'var(--primary-color)', textDecoration: 'none', fontSize: '0.875rem', marginBottom: '1rem', cursor: 'pointer' }} onClick={() => setStep(2)}>&larr; Change date</a>
          <h2 style={{ fontSize: '1.1rem', marginBottom: '0.25rem' }}>Pick a Time</h2>
          <p className="text-sm text-muted" style={{ marginBottom: '1rem' }}>
            {new Date(selectedDate + 'T12:00:00Z').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}
          </p>
          {slotsLoading ? (
            <div style={{ margin: '1rem auto', textAlign: 'center' }}><Spinner /></div>
          ) : slots.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem 1rem', color: 'var(--text-muted)' }}>No availability on this date. Try another day.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
              {slots.map((s) => (
                <div
                  key={`${s.time}-${s.providerUid}`}
                  style={{
                    padding: '0.625rem 0.5rem', border: `2px solid ${selectedSlot?.time === s.time ? 'var(--primary-color)' : 'var(--border-color)'}`,
                    borderRadius: 'var(--radius)', textAlign: 'center', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500,
                    background: selectedSlot?.time === s.time ? 'var(--primary-color)' : undefined,
                    color: selectedSlot?.time === s.time ? '#fff' : undefined,
                  }}
                  onClick={() => { setSelectedSlot(s); setStep(4); }}
                >
                  {formatTime(s.time)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 4: Pick Visit Type (medium) */}
      {step === 4 && selectedSlot && (
        <div className="card">
          <a style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: 'var(--primary-color)', textDecoration: 'none', fontSize: '0.875rem', marginBottom: '1rem', cursor: 'pointer' }} onClick={() => setStep(3)}>&larr; Change time</a>
          <h2 style={{ fontSize: '1.1rem', marginBottom: '0.25rem' }}>How would you like to meet?</h2>
          <p className="text-sm text-muted" style={{ marginBottom: '1rem' }}>
            Pick the option that works best for you.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {VISIT_MEDIUM_OPTIONS.map((opt) => {
              const isSelected = medium === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  data-medium={opt.value}
                  onClick={() => { setMedium(opt.value); setStep(5); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    width: '100%', textAlign: 'left',
                    padding: '0.875rem 1rem',
                    border: `2px solid ${isSelected ? 'var(--primary-color)' : 'var(--border-color)'}`,
                    borderRadius: 'var(--radius)',
                    background: isSelected ? 'var(--primary-color)' : 'transparent',
                    color: isSelected ? '#fff' : undefined,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: '0.125rem' }}>{opt.label}</div>
                    <div style={{ fontSize: '0.8rem', color: isSelected ? 'rgba(255,255,255,0.9)' : 'var(--text-muted)', lineHeight: 1.35 }}>
                      {opt.description}
                    </div>
                  </div>
                  <span
                    aria-hidden="true"
                    data-testid="medium-arrow"
                    style={{ fontSize: '1.25rem', lineHeight: 1, flexShrink: 0, opacity: isSelected ? 0.95 : 0.5 }}
                  >
                    &rarr;
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 5: Enter Info + Confirm */}
      {step === 5 && selectedSlot && (
        <div className="card">
          <a style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: 'var(--primary-color)', textDecoration: 'none', fontSize: '0.875rem', marginBottom: '1rem', cursor: 'pointer' }} onClick={() => setStep(4)}>&larr; Change visit type</a>
          <h2 style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>Confirm Your Visit</h2>

          <div style={{ background: 'var(--bg-color)', borderRadius: 'var(--radius)', padding: '1rem', marginBottom: '1.25rem' }}>
            {[
              { label: 'Provider', value: selectedSlot.providerName },
              { label: 'Date', value: new Date(selectedDate + 'T12:00:00Z').toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', timeZone: 'UTC' }) },
              { label: 'Time', value: formatTime(selectedSlot.time) },
              { label: 'Duration', value: `${slotDuration} minutes` },
              ...(medium ? [{ label: 'Visit Type', value: MEDIUM_LABELS[medium] }] : []),
            ].map((row) => (
              <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--border-color)', fontSize: '0.9rem' }}>
                <span style={{ color: 'var(--text-muted)' }}>{row.label}</span>
                <span>{row.value}</span>
              </div>
            ))}
          </div>

          {error && <div style={{ color: 'var(--error-color)', fontSize: '0.875rem', marginBottom: '0.75rem' }}>{error}</div>}

          <div className="form-group">
            <label>Your Name <span style={{ color: 'var(--error-color)' }}>*</span></label>
            <input type="text" placeholder="Full name" maxLength={200} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Email <span className="text-muted text-sm">(optional, for calendar invite)</span></label>
            <input type="email" placeholder="your@email.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="form-group">
            <label>
              Phone {medium === 'sms' ? <span style={{ color: 'var(--error-color)' }}>*</span> : <span className="text-muted text-sm">(optional)</span>}
            </label>
            <PhoneInput value={phone} onChange={setPhone} />
            {medium === 'sms' && (
              <div className="text-sm text-muted" style={{ marginTop: '0.25rem' }}>
                Required for text-message visits. Your provider will text this number.
              </div>
            )}
          </div>
          {(phone.trim().length > 0 || medium === 'sms') && (
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', width: '100%', cursor: 'pointer', fontWeight: 'normal' }}>
                <input
                  type="checkbox"
                  checked={smsConsent}
                  onChange={(e) => setSmsConsent(e.target.checked)}
                  style={{ width: '16px', height: '16px', flexShrink: 0, marginTop: '3px' }}
                />
                <span className="text-sm" style={{ flex: 1, minWidth: 0 }}>
                  {medium === 'sms'
                    ? 'I agree to receive SMS messages from this practice for my visit. Msg & data rates may apply. Reply STOP to opt out.'
                    : 'I agree to receive SMS visit reminders. Msg & data rates may apply. Reply STOP to opt out.'} <SmsConsentDisclosure intro="Full SMS consent disclosure:" script={SMS_CONSENT_SCRIPT_PATIENT} />
                </span>
              </label>
            </div>
          )}

          <button className="btn btn--primary btn--full" onClick={handleBook} disabled={booking}>
            {booking ? 'Booking...' : 'Book Visit'}
          </button>
        </div>
      )}

      {/* Step 6: Confirmation */}
      {step === 6 && confirmData && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 'var(--radius)', padding: '1.5rem', textAlign: 'center' }}>
          <h3 style={{ color: '#166534', marginBottom: '0.5rem' }}>Visit Booked!</h3>
          <p className="text-sm" style={{ marginBottom: '1rem', color: '#166534' }}>Your telehealth visit has been confirmed.</p>

          <div style={{ background: '#fff', borderRadius: 'var(--radius)', padding: '1rem', textAlign: 'left', marginBottom: '1rem' }}>
            {[
              { label: 'Provider', value: confirmData.providerName },
              { label: 'Date', value: new Date(selectedDate + 'T12:00:00Z').toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', timeZone: 'UTC' }) },
              { label: 'Time', value: selectedSlot ? formatTime(selectedSlot.time) : '' },
              { label: 'Duration', value: `${confirmData.duration} minutes` },
              ...(medium ? [{ label: 'Visit Type', value: MEDIUM_LABELS[medium] }] : []),
            ].map((row) => (
              <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--border-color)', fontSize: '0.9rem' }}>
                <span style={{ color: 'var(--text-muted)' }}>{row.label}</span>
                <span>{row.value}</span>
              </div>
            ))}
          </div>

          {medium === 'video' && confirmData.joinUrl && (
            <>
              <a href={new URL(confirmData.joinUrl, window.location.origin).href} className="btn btn--primary btn--full" style={{ display: 'block', textDecoration: 'none', textAlign: 'center' }}>Join Visit Link</a>
              <p className="text-sm text-muted" style={{ marginTop: '0.75rem' }}>Save this link. Use it to join your visit at the scheduled time. No account needed.</p>
            </>
          )}
          {medium === 'phone' && confirmData.dialIn && (
            <div style={{ background: '#fff', borderRadius: 'var(--radius)', padding: '1rem', textAlign: 'left' }}>
              <div className="text-sm" style={{ marginBottom: '0.5rem', fontWeight: 600 }}>How to join your phone visit:</div>
              <div style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                Call <strong>{formatPhoneDashed(confirmData.dialIn.phone)}</strong> at your appointment time
              </div>
              <div style={{ fontSize: '0.875rem' }}>
                Enter PIN: <strong>{confirmData.dialIn.patientPin || confirmData.dialIn.pin}</strong>
              </div>
            </div>
          )}
          {medium === 'sms' && (
            <div style={{ background: '#fff', borderRadius: 'var(--radius)', padding: '1rem', textAlign: 'left' }}>
              <div className="text-sm" style={{ marginBottom: '0.5rem', fontWeight: 600 }}>How your text-message visit works:</div>
              {confirmData.smsNumber ? (
                <div style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                  Your provider will text you from <strong>{formatPhoneDashed(confirmData.smsNumber)}</strong> at your appointment time.
                </div>
              ) : (
                <div style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                  Your provider will text you at your appointment time.
                </div>
              )}
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                Save this number in your contacts so you don't miss the message.
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
        <p className="text-sm text-muted">Powered by <a href="https://simplytelehealth.md" style={{ color: 'var(--primary-color)', textDecoration: 'none' }}>SimplyTelehealth</a></p>
      </div>
    </div>
  );
}
